import React from 'react';

import InputAdornment from '@material-ui/core/InputAdornment';
import ErrorIcon from '@material-ui/icons/Error';
import PropTypes from 'prop-types';

import { errorIconStyle, TextFieldWrapper } from './TextField.styled';

const TextField = React.forwardRef((props, ref) => {
  const {
    id,
    type,
    value,
    label,
    disabled,
    autoFocus,
    className,
    multiline,
    helperText,
    error,
    shrinkLabel,
    useErrorIcon,
    placeholder,
    startAdornment,
    endAdornment,
    accessibilityLabel,
    rows,
    rowsMax,
    onChange,
    defaultValue,
    inputRef,
    onKeyDown,
    name,
    'data-testid': dataTestId,
    'aria-describedby': ariaDescribedBy,
    onBlur,
    onFocus,
    onMouseLeave,
    onMouseOver,
    onTouchEnd,
    onTouchStart,
  } = props;

  return (
    <TextFieldWrapper
      ref={ref}
      name={name}
      id={id}
      inputRef={inputRef}
      onKeyDown={onKeyDown}
      margin="dense"
      variant="outlined"
      type={type}
      value={value}
      defaultValue={defaultValue}
      label={label}
      disabled={disabled}
      autoFocus={autoFocus}
      className={className}
      multiline={multiline}
      helperText={helperText}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseLeave={onMouseLeave}
      onMouseOver={onMouseOver}
      onTouchEnd={onTouchEnd}
      onTouchStart={onTouchStart}
      error={error}
      rows={rows}
      rowsMax={rowsMax}
      data-testid={dataTestId}
      InputProps={{
        classes: {
          input: 'fieldset-textfield-input',
          focused: 'fieldset-textfield-focusedInput',
          error: 'fieldset-textfield-error',
          multiline: 'fieldset-textfield-multiline',
          root: 'inputbase-root',
        },
        placeholder: placeholder || label,
        endAdornment:
          error && useErrorIcon ? (
            <InputAdornment position="end">
              <ErrorIcon style={errorIconStyle} aria-label="error-icon" />
            </InputAdornment>
          ) : (
            endAdornment
          ),
        startAdornment,
        'aria-label': accessibilityLabel || label,
      }}
      InputLabelProps={{
        shrink: shrinkLabel,
        classes: {
          root: multiline
            ? 'fieldset-multiline-label'
            : 'fieldset-textfield-label',
          shrink: 'fieldset-textfield-shrunkLabel',
          focused: 'fieldset-textfield-focusedLabel',
          error: 'fieldset-textfield-errorLabel',
          outlined: 'fieldset-textfield-outlined',
        },
      }}
      FormHelperTextProps={{
        classes: {
          error: 'fieldset-textfield-helper-text-error',
          ...(!error && { focused: 'fieldset-textfield-helper-text-focused' }),
        },
      }}
      aria-describedby={ariaDescribedBy}
    />
  );
});

TextField.displayName = 'TextField';

TextField.propTypes = {
  id: PropTypes.string,
  type: PropTypes.oneOf(['text', 'number', 'password']),
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  multiline: PropTypes.bool,
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  error: PropTypes.bool,
  useErrorIcon: PropTypes.bool,
  accessibilityLabel: PropTypes.string,
  placeholder: PropTypes.string,
  startAdornment: PropTypes.object,
  endAdornment: PropTypes.object,
  shrinkLabel: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onMouseOver: PropTypes.func,
  onTouchEnd: PropTypes.func,
  onTouchStart: PropTypes.func,
  onKeyDown: PropTypes.func,
  rows: PropTypes.number,
  rowsMax: PropTypes.number,
  'data-testid': PropTypes.string,
  'aria-describedby': PropTypes.string,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(HTMLInputElement) }),
  ]),
  name: PropTypes.string,
};

TextField.defaultProps = {
  id: '',
  type: 'text',
  value: undefined,
  defaultValue: undefined,
  label: '',
  disabled: false,
  autoFocus: false,
  className: undefined,
  multiline: false,
  helperText: '',
  error: false,
  useErrorIcon: true,
  accessibilityLabel: undefined,
  placeholder: '',
  startAdornment: undefined,
  endAdornment: undefined,
  shrinkLabel: undefined,
  rows: undefined,
  rowsMax: undefined,
  onChange: () => {},
  onBlur: () => {},
  onFocus: () => {},
  onMouseLeave: () => {},
  onMouseOver: () => {},
  onTouchEnd: () => {},
  onTouchStart: () => {},
  onKeyDown: () => {},
  'data-testid': undefined,
  'aria-describedby': undefined,
  inputRef: () => {},
  name: '',
};

export default TextField;
